import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import { useFasesDosVendedores, FaseBadge, faseTexto } from '../components/FaseBadge';
import './AppVersions.css';

// Sentinela do filtro "(sem versão)" — precisa casar com _NO_VERSION do pushtokens_routes.py.
const NO_VERSION = '__null__';

const LINK_LABEL = {
    active: null,                 // vendedor normal: sem badge
    inactive: 'inativo',
    removed: 'removido',
    unlinked: 'sem vínculo',
};

// A API já devolve ISO naive em horário de Brasília (converte de UTC na fonte, ver
// _utc_to_br_iso em pushtokens_routes.py). Formatamos direto dos componentes da string,
// sem passar por new Date(): qualquer round-trip por Date reinterpreta a string no fuso
// do navegador e volta a deslocar a hora.
const fmtDate = (iso) => {
    if (!iso) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
    if (!m) return iso;
    const [, y, mo, d, h, mi] = m;
    return `${d}/${mo}/${y.slice(2)} ${h}:${mi}`;
};

const versionLabel = (v) => v || '(sem versão)';

function AppVersions({ user }) {
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ── Tabela geral (carrega ao abrir a tela, sem escolher nada) ──────────────
    const [overview, setOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [includeInternal, setIncludeInternal] = useState(false);

    // ── Detalhamento (abre ao escolher empresa/loja) ───────────────────────────
    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [salesmen, setSalesmen] = useState([]);
    const [subdomain, setSubdomain] = useState('');
    const { fases: fasesVend } = useFasesDosVendedores({ subdomain });
    const [storeId, setStoreId] = useState('');
    const [salesmanId, setSalesmanId] = useState('');
    const [appVersion, setAppVersion] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [showAllRows, setShowAllRows] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [openHistory, setOpenHistory] = useState(null);   // id da linha expandida
    const [copiedId, setCopiedId] = useState(null);

    const userId = user?.user_id || user?.id;

    const loadOverview = useCallback(async () => {
        setLoadingOverview(true);
        try {
            const r = await apiService.pushtokensOverview({ include_internal: includeInternal });
            setOverview(r);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar a visão geral', 'error');
        } finally {
            setLoadingOverview(false);
        }
    }, [includeInternal, notify]);

    useEffect(() => { loadOverview(); }, [loadOverview]);

    // Empresas para o seletor do detalhamento
    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                notify('Erro ao carregar empresas', 'error');
            }
        })();
    }, [notify]);

    // Lojas ao trocar empresa
    useEffect(() => {
        setStores([]); setStoreId(''); setSalesmanId(''); setSalesmen([]);
        setDetail(null); setOpenHistory(null);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
        })();
    }, [subdomain, notify]);

    // Vendedores ao escolher empresa/loja
    useEffect(() => {
        if (!subdomain) { setSalesmen([]); return; }
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: storeId || undefined });
                setSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso: o dropdown de vendedor é opcional */ }
        })();
    }, [subdomain, storeId, userId]);

    const loadDetail = useCallback(async () => {
        if (!subdomain) { setDetail(null); return; }
        setLoadingDetail(true); setOpenHistory(null);
        try {
            const r = await apiService.pushtokensList({
                subdomain,
                store_id: storeId ? Number(storeId) : undefined,
                salesman_id: salesmanId ? Number(salesmanId) : undefined,
                app_version: appVersion || undefined,
                include_deleted: includeDeleted,
                show_all_rows: showAllRows,
            });
            setDetail(r);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar os aparelhos', 'error');
        } finally {
            setLoadingDetail(false);
        }
    }, [subdomain, storeId, salesmanId, appVersion, includeDeleted, showAllRows, notify]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    // Clique numa célula da tabela geral → abre o detalhamento já filtrado.
    const drillDown = (sd, version) => {
        setSubdomain(sd);
        setStoreId(''); setSalesmanId('');
        setAppVersion(version || '');
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    };

    const copyToken = async (row) => {
        try {
            await navigator.clipboard.writeText(row.token);
            setCopiedId(row.id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch (e) {
            notify('Não foi possível copiar o token', 'error');
        }
    };

    const topVersions = overview?.top_versions || [];
    const companyName = companies.find((c) => c.subdomain === subdomain)?.name || subdomain;

    return (
        <div className="av-page">
            {toast && <div className={`av-toast av-toast-${toast.type}`}>{toast.msg}</div>}

            <h1 className="av-title">📱 Versões do app</h1>
            <p className="av-help">
                Aparelhos registrados por cliente e a versão do app em cada um. A <strong>pushtokens</strong> guarda
                uma linha por token do Firebase — o token roda a cada reinstalação e a string do aparelho inclui a
                versão do Android, então há vários registros por vendedor. Aqui conta-se{' '}
                <strong>1 aparelho por vendedor</strong>, sempre o registro mais recente.
                Datas em <strong>horário de Brasília</strong>.
            </p>

            {/* ── Tabela geral ─────────────────────────────────────────────── */}
            <div className="av-section">
                <div className="av-section-head">
                    <h2>Visão geral por cliente</h2>
                    <div className="av-section-actions">
                        <label className="av-check">
                            <input type="checkbox" checked={includeInternal}
                                onChange={(e) => setIncludeInternal(e.target.checked)} />
                            Incluir demo/admin
                        </label>
                        <button className="av-btn av-btn-sm" onClick={loadOverview} disabled={loadingOverview}>
                            {loadingOverview ? 'Carregando…' : '↻ Atualizar'}
                        </button>
                    </div>
                </div>

                {loadingOverview && !overview && <p className="av-empty">Carregando visão geral…</p>}

                {overview && (
                    <>
                        <p className="av-meta">
                            {overview.totals.devices} aparelhos em {overview.totals.clients} clientes ·{' '}
                            {overview.totals.versions} versões distintas · mais nova:{' '}
                            <strong>{overview.newest_version || '—'}</strong>
                        </p>
                        <div className="av-table-wrap">
                            <table className="av-table av-table-overview">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th className="av-num">Aparelhos</th>
                                        <th className="av-num">Ativos</th>
                                        <th className="av-num av-soft">Inativos</th>
                                        <th className="av-num av-soft">Sem vínculo</th>
                                        {topVersions.map((v) => <th key={v} className="av-num">{v}</th>)}
                                        <th className="av-num">Anteriores</th>
                                        <th className="av-num av-soft">Sem versão</th>
                                        <th className="av-num">% na atual</th>
                                        <th>Último sinal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.clients.map((c) => {
                                        const st = c.by_link_status || {};
                                        const inativos = (st.inactive || 0) + (st.removed || 0);
                                        return (
                                            <tr key={c.subdomain}>
                                                <td>
                                                    <button className="av-link" onClick={() => drillDown(c.subdomain, '')}>
                                                        {c.name}
                                                    </button>
                                                    <div className="av-sub">
                                                        {c.subdomain} · {c.oldest_version || '—'} → {c.newest_version || '—'}
                                                    </div>
                                                </td>
                                                <td className="av-num av-strong">{c.total}</td>
                                                <td className="av-num">{st.active || 0}</td>
                                                <td className="av-num av-soft">{inativos || '—'}</td>
                                                <td className="av-num av-soft">{st.unlinked || '—'}</td>
                                                {topVersions.map((v) => {
                                                    const n = c.by_version?.[v] || 0;
                                                    return (
                                                        <td key={v} className="av-num">
                                                            {n === 0 ? <span className="av-zero">—</span> : (
                                                                <button className="av-cell-link"
                                                                    onClick={() => drillDown(c.subdomain, v)}>{n}</button>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td className="av-num">
                                                    {c.older === 0 ? <span className="av-zero">—</span> : (
                                                        <button className="av-cell-link av-cell-old"
                                                            onClick={() => drillDown(c.subdomain, '')}>{c.older}</button>
                                                    )}
                                                </td>
                                                <td className="av-num av-soft">
                                                    {c.no_version === 0 ? <span className="av-zero">—</span> : (
                                                        <button className="av-cell-link"
                                                            onClick={() => drillDown(c.subdomain, NO_VERSION)}>{c.no_version}</button>
                                                    )}
                                                </td>
                                                <td className="av-num">
                                                    {c.pct_updated === null ? '—' : (
                                                        <span className={`av-pct ${c.pct_updated >= 80 ? 'av-pct-ok'
                                                            : c.pct_updated >= 40 ? 'av-pct-warn' : 'av-pct-bad'}`}>
                                                            {c.pct_updated}%
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="av-date">{fmtDate(c.last_seen)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p className="av-note">
                            <strong>% na atual</strong> = aparelhos de vendedores <em>ativos</em> na versão{' '}
                            {overview.newest_version || '—'}. Vendedores inativos, removidos e tokens sem vínculo
                            aparecem nas colunas em cinza (informativo) e não entram nesse cálculo.
                            Clique num número para abrir o detalhamento.
                        </p>
                    </>
                )}
            </div>

            {/* ── Detalhamento por vendedor ────────────────────────────────── */}
            <div className="av-section">
                <div className="av-section-head">
                    <h2>Detalhamento por vendedor{subdomain ? ` — ${companyName}` : ''}</h2>
                </div>

                <div className="av-filters">
                    <div className="av-field">
                        <label>Empresa</label>
                        <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                            <option value="">— Selecione —</option>
                            {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="av-field">
                        <label>Loja</label>
                        <select value={storeId} onChange={(e) => { setStoreId(e.target.value); setSalesmanId(''); }}
                            disabled={!subdomain}>
                            <option value="">Todas</option>
                            {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                        </select>
                    </div>
                    <div className="av-field">
                        <label>Vendedor</label>
                        <select value={salesmanId} onChange={(e) => setSalesmanId(e.target.value)} disabled={!subdomain}>
                            <option value="">Todos</option>
                            {salesmen.map((s) => (
                                <option key={s.salesman_id} value={s.salesman_id}>
                                    {s.salesman_name}{s.is_active ? '' : ' (inativo)'}{faseTexto(fasesVend[String(s.salesman_id)], { comDia: false })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="av-field">
                        <label>Versão do app</label>
                        <select value={appVersion} onChange={(e) => setAppVersion(e.target.value)} disabled={!subdomain}>
                            <option value="">Todas</option>
                            {(detail?.app_versions || []).map((v) => <option key={v} value={v}>{v}</option>)}
                            {detail?.has_no_version && <option value={NO_VERSION}>(sem versão)</option>}
                        </select>
                    </div>
                    <div className="av-field av-field-checks">
                        <label className="av-check">
                            <input type="checkbox" checked={showAllRows} disabled={!subdomain}
                                onChange={(e) => setShowAllRows(e.target.checked)} />
                            Mostrar todas as linhas
                        </label>
                        <label className="av-check">
                            <input type="checkbox" checked={includeDeleted} disabled={!subdomain}
                                onChange={(e) => setIncludeDeleted(e.target.checked)} />
                            Incluir excluídos
                        </label>
                    </div>
                </div>

                {!subdomain && (
                    <p className="av-empty">
                        Selecione uma empresa (ou clique num número da tabela acima) para ver os aparelhos por vendedor.
                    </p>
                )}

                {subdomain && loadingDetail && !detail && <p className="av-empty">Carregando aparelhos…</p>}

                {subdomain && detail && (
                    <>
                        {detail.summary.length > 0 && (
                            <div className="av-chips">
                                {detail.summary.map((s) => {
                                    const val = s.app_version === null ? NO_VERSION : s.app_version;
                                    const active = appVersion === val;
                                    return (
                                        <button key={val}
                                            className={`av-chip ${active ? 'av-chip-active' : ''}`}
                                            onClick={() => setAppVersion(active ? '' : val)}>
                                            {versionLabel(s.app_version)} — {s.devices}{' '}
                                            {s.devices === 1 ? 'aparelho' : 'aparelhos'}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <p className="av-meta">
                            {showAllRows
                                ? `${detail.rows.length} linhas (tabela crua) de ${detail.total_rows}`
                                : `${detail.rows.length} de ${detail.total_devices} aparelhos · ${detail.total_rows} linhas na tabela`}
                            {loadingDetail && ' · atualizando…'}
                        </p>

                        {detail.duplicated_names.length > 0 && (
                            <p className="av-warn">
                                ⚠ {detail.duplicated_names.length === 1 ? 'Um vendedor aparece' : 'Vendedores aparecem'}{' '}
                                mais de uma vez ({detail.duplicated_names.join(', ')}) — são cadastros distintos de{' '}
                                <code>salesman</code> para a mesma pessoa, provavelmente recadastro em vez de reativação.
                            </p>
                        )}

                        {detail.rows.length === 0 && <p className="av-empty">Nenhum aparelho neste filtro.</p>}

                        {detail.rows.length > 0 && (
                            <div className="av-table-wrap">
                                <table className="av-table">
                                    <thead>
                                        <tr>
                                            <th>Vendedor</th>
                                            <th>Loja</th>
                                            <th>Aparelho</th>
                                            <th>Versão</th>
                                            <th>Token</th>
                                            <th className="av-num">Registros</th>
                                            <th>Criado</th>
                                            <th>Último sinal</th>
                                            {includeDeleted && <th>Excluído</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.rows.map((row) => {
                                            const badge = LINK_LABEL[row.link_status];
                                            const hist = detail.history[String(row.id)] || [];
                                            const open = openHistory === row.id;
                                            return (
                                                <React.Fragment key={row.id}>
                                                    <tr className={row.link_status !== 'active' ? 'av-row-soft' : ''}>
                                                        <td>
                                                            {row.salesman_name || <em>— sem vendedor —</em>}<FaseBadge fase={fasesVend[String(row.salesman_id)]} compacto />
                                                            {badge && <span className="av-badge">{badge}</span>}
                                                            {row.is_device_owner && (
                                                                <span className="av-badge av-badge-owner">owner</span>
                                                            )}
                                                        </td>
                                                        <td>{row.store_name || '—'}</td>
                                                        <td>{row.device || '—'}</td>
                                                        <td className="av-version">{versionLabel(row.app_version)}</td>
                                                        <td>
                                                            <button className="av-token" title={row.token}
                                                                onClick={() => copyToken(row)}>
                                                                {row.token.slice(0, 14)}…
                                                                <span className="av-token-hint">
                                                                    {copiedId === row.id ? '✓ copiado' : 'copiar'}
                                                                </span>
                                                            </button>
                                                        </td>
                                                        <td className="av-num">
                                                            {hist.length > 0 ? (
                                                                <button className="av-cell-link"
                                                                    onClick={() => setOpenHistory(open ? null : row.id)}>
                                                                    {row.registros} {open ? '▴' : '▾'}
                                                                </button>
                                                            ) : row.registros}
                                                        </td>
                                                        <td className="av-date">{fmtDate(row.created)}</td>
                                                        <td className="av-date">{fmtDate(row.last_seen)}</td>
                                                        {includeDeleted && (
                                                            <td className="av-date av-deleted">{fmtDate(row.deleted)}</td>
                                                        )}
                                                    </tr>
                                                    {open && hist.map((h) => (
                                                        <tr key={h.id} className="av-row-hist">
                                                            <td className="av-hist-label">↳ registro anterior</td>
                                                            <td>{h.store_name || '—'}</td>
                                                            <td>{h.device || '—'}</td>
                                                            <td className="av-version">{versionLabel(h.app_version)}</td>
                                                            <td>
                                                                <button className="av-token" title={h.token}
                                                                    onClick={() => copyToken(h)}>
                                                                    {h.token.slice(0, 14)}…
                                                                    <span className="av-token-hint">
                                                                        {copiedId === h.id ? '✓ copiado' : 'copiar'}
                                                                    </span>
                                                                </button>
                                                            </td>
                                                            <td className="av-num">—</td>
                                                            <td className="av-date">{fmtDate(h.created)}</td>
                                                            <td className="av-date">{fmtDate(h.last_seen)}</td>
                                                            {includeDeleted && (
                                                                <td className="av-date av-deleted">{fmtDate(h.deleted)}</td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default AppVersions;
